/**
 * Cliente HTTP de la API.
 *
 * Tres decisiones que vienen del encargo y que se ven todas acá:
 *
 * - **El token de acceso vive en memoria, nunca en el almacenamiento del navegador.** Lo
 *   que se guarda en `localStorage` lo puede leer cualquier script inyectado; lo que vive
 *   en una variable del módulo se va con la pestaña. La sesión sobrevive igual a un
 *   recargado, porque la renovación viaja en una cookie que el código no toca.
 * - **El servidor responde códigos de error**, así que el cliente nunca muestra lo que
 *   venga en el cuerpo: traduce el código con el catálogo de idioma (D5).
 * - **Cada pedido lleva su identificador de correlación** (D8), para que un problema que
 *   reporta un comercio se encuentre en el registro del servidor sin adivinar.
 */

export class ErrorDeApi extends Error {
  constructor(
    readonly codigo: string,
    readonly detalles: Record<string, unknown>,
    readonly estado: number,
    readonly correlacion: string | null,
  ) {
    super(codigo);
    this.name = 'ErrorDeApi';
  }
}

const ENCABEZADO_DE_CORRELACION = 'x-correlacion';
const BASE = '/api';

let tokenDeAcceso: string | null = null;
let renovacionEnCurso: Promise<boolean> | null = null;
let alPerderLaSesion: (() => void) | null = null;

export function guardarToken(token: string | null): void {
  tokenDeAcceso = token;
}

export function hayToken(): boolean {
  return tokenDeAcceso !== null;
}

export function avisarCuandoSePierdaLaSesion(aviso: () => void): void {
  alPerderLaSesion = aviso;
}

function nuevaCorrelacion(): string {
  return globalThis.crypto?.randomUUID?.() ?? `web-${String(Date.now())}`;
}

/**
 * Renueva el token de acceso con la cookie.
 *
 * Se comparte una sola promesa: si tres consultas caducan a la vez —cosa normal en una
 * pantalla con varias tablas— tienen que esperar la misma renovación y no disparar tres,
 * que además rotarían el token entre ellas y dispararían la detección de reuso.
 */
async function renovar(): Promise<boolean> {
  renovacionEnCurso ??= (async () => {
    try {
      const respuesta = await fetch(`${BASE}/acceso/renovacion`, {
        method: 'POST',
        credentials: 'include',
        headers: { [ENCABEZADO_DE_CORRELACION]: nuevaCorrelacion() },
      });
      if (!respuesta.ok) return false;
      const { tokenDeAcceso: token } = (await respuesta.json()) as { tokenDeAcceso: string };
      tokenDeAcceso = token;
      return true;
    } catch {
      return false;
    } finally {
      renovacionEnCurso = null;
    }
  })();

  return renovacionEnCurso;
}

/** Al arrancar: si hay cookie viva, se entra sin volver a pedir la contraseña. */
export async function recuperarSesion(): Promise<boolean> {
  return renovar();
}

interface OpcionesDePedido extends Omit<RequestInit, 'body'> {
  readonly cuerpo?: unknown;
  /**
   * El cuerpo tal cual, sin serializar ni declararle tipo de contenido. Lo usa la subida de
   * archivos: un `FormData` lleva un separador que elige el navegador, y declararlo a mano lo
   * rompe.
   */
  readonly cuerpoCrudo?: BodyInit;
  /** Uso interno: evita reintentar en bucle si la renovación tampoco sirve. */
  readonly sinReintento?: boolean;
}

/**
 * Sube un archivo.
 *
 * Va aparte de `pedir` porque **un `FormData` no lleva `Content-Type`**: lo pone el navegador con
 * el separador que él elige, y declararlo a mano rompe el pedido de una forma que se ve como «los
 * datos enviados no son válidos». Todo lo demás —el token, la correlación, el reintento al
 * caducar— es lo mismo, así que se reusa.
 */
export async function subirArchivo<T>(ruta: string, archivo: File): Promise<T> {
  const formulario = new FormData();
  formulario.append('archivo', archivo);
  return pedir<T>(ruta, { method: 'POST', cuerpoCrudo: formulario });
}

/**
 * Trae un archivo del servidor y devuelve una dirección que el navegador puede dibujar.
 *
 * ## Por qué no alcanza con `<img src="/api/archivos/…">`
 *
 * Porque **el token vive en memoria y viaja como encabezado**, y una etiqueta `img` —o un enlace,
 * o un `iframe`— la pide el navegador solo, sin encabezados nuestros: llega sin autorización y el
 * servidor contesta 401. La imagen no se ve y no hay ningún error a la vista, porque una imagen
 * que no carga no rompe nada. Así que el archivo lo pide el código, con su token, y se lo entrega
 * al navegador como una dirección de objeto.
 *
 * Quien la use tiene que soltarla con `URL.revokeObjectURL` al dejar de mostrarla: mientras exista,
 * el contenido queda en memoria.
 */
export async function pedirArchivo(ruta: string): Promise<string> {
  const respuesta = await pedirCrudo(ruta, {});
  return URL.createObjectURL(await respuesta.blob());
}

export async function pedir<T>(ruta: string, opciones: OpcionesDePedido = {}): Promise<T> {
  const respuesta = await pedirCrudo(ruta, opciones);

  if (respuesta.status === 204) return undefined as T;

  // Un cuerpo vacío con 200 también es una respuesta válida, y tratarla como error es peor que
  // inútil: la operación **ya ocurrió** en el servidor, y la pantalla muestra un fallo y no
  // refresca. Costó un rato de depuración descubrirlo, así que queda cubierto acá y no en cada
  // llamada.
  const texto = await respuesta.text();
  return (texto === '' ? undefined : JSON.parse(texto)) as T;
}

/** El pedido con todo lo común —token, correlación, renovación, error— y sin interpretar el cuerpo. */
async function pedirCrudo(ruta: string, opciones: OpcionesDePedido): Promise<Response> {
  const { cuerpo, cuerpoCrudo, sinReintento, ...resto } = opciones;

  const respuesta = await fetch(`${BASE}${ruta}`, {
    ...resto,
    credentials: 'include',
    headers: {
      // El tipo de contenido sólo cuando hay contenido. Un POST sin cuerpo que igual se declara
      // `application/json` lo rechaza Fastify con un 400 antes de llegar al controlador, y el
      // error que se ve es "los datos enviados no son válidos" para un pedido que no manda datos.
      // Se descubrió cerrando un ejercicio; afectaba también a las acciones de compras.
      ...(cuerpo === undefined ? {} : { 'Content-Type': 'application/json' }),
      [ENCABEZADO_DE_CORRELACION]: nuevaCorrelacion(),
      ...(tokenDeAcceso === null ? {} : { authorization: `Bearer ${tokenDeAcceso}` }),
      ...resto.headers,
    },
    ...(cuerpo === undefined ? {} : { body: JSON.stringify(cuerpo) }),
    ...(cuerpoCrudo === undefined ? {} : { body: cuerpoCrudo }),
  });

  if (respuesta.status === 401 && sinReintento !== true) {
    // El token de acceso dura quince minutos: que caduque en medio de una jornada es lo
    // normal, no un error. Se renueva y se reintenta una vez, sin que la persona lo note.
    if (await renovar()) {
      return pedirCrudo(ruta, { ...opciones, sinReintento: true });
    }
    tokenDeAcceso = null;
    alPerderLaSesion?.();
  }

  if (!respuesta.ok) {
    const cuerpoDelError = (await respuesta.json().catch(() => ({}))) as {
      codigo?: string;
      detalles?: Record<string, unknown>;
    };
    throw new ErrorDeApi(
      cuerpoDelError.codigo ?? 'ERR_INTERNO',
      cuerpoDelError.detalles ?? {},
      respuesta.status,
      respuesta.headers.get(ENCABEZADO_DE_CORRELACION),
    );
  }

  return respuesta;
}
